import { useEffect, useCallback } from "react";
import { useNavigate, Routes, Route, Navigate } from "react-router-dom";
// import { createMuiTheme } from "@mui/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enGB from "date-fns/locale/en-GB";
import { jwtDecode } from "jwt-decode";

import {
  getProfile as apiGetProfile,
  getPlayerDetails as apiGetPlayerDetails,
  refreshToken as apiRefreshToken,
  loginToClub as apiLoginToClub,
  getOrganizationData as apiGetOrganizationData,
  getClub as apiGetClub,
} from "./services/api";

import LoginPage from "./ui/pages/loginPage/LoginPage";
import ForgotPasswordPage from "./ui/pages/forgotPasswordPage/ForgotPasswordPage";
import ResetPasswordPage from "./ui/pages/resetPasswordPage/ResetPasswordPage";
import AcceptInvitePage from "./ui/pages/acceptInvitePage/AcceptInvitePage";
import VerifyAccountPage from "./ui/pages/verifyAccountPage/VerifyAccountPage";
import SplashPage from "./ui/pages/splashPage/SplashPage";
import NoClubsPage from "./ui/pages/noClubsPage/NoClubsPage";
import AdminEventsPage from "./ui/pages/adminEventsPage/AdminEventsPage";
import EventsPage from "./ui/pages/eventsPage/EventsPage";
import EventPage from "./ui/pages/eventPage/EventPage";
import CreateEventPage from "./ui/pages/createEventPage/CreateEventPage";
import EditEventPage from "./ui/pages/editEventPage/EditEventPage";
import ProfilePage from "./ui/pages/profilePage/ProfilePage";
import ArchivePage from "./ui/pages/archivePage/ArchivePage";
import ArchivedEventPage from "./ui/pages/archivedEventPage/ArchivedEventPage";
import OutstandingFeesPage from "./ui/pages/outstandingFeesPage/OutstandingFeesPage.js";
import CompleteMandateSetupPage from "./ui/pages/completeMandateSetupPage/CompleteMandateSetupPage";
import AvailabilityPage from "./ui/pages/availabilityPage/AvailabilityPage";
import AdminAvailabilityPage from "./ui/pages/adminAvailabilityPage/AdminAvailabilityPage";
import AvailabilityRequestPage from "./ui/pages/availabilityRequestPage/AvailabilityRequestPage";
import CreateAvailabilityRequestPage from "./ui/pages/createAvailabilityRequestPage/CreateAvailabilityRequestPage";
import EditAvailabilityRequestPage from "./ui/pages/editAvailabilityRequestPage/EditAvailabilityRequestPage";

import AdminLoginPage from "./ui/pages/adminLoginPage/AdminLoginPage";
import AdminSelectUserPage from "./ui/pages/adminSelectUserPage/AdminSelectUserPage";

import { withState } from "./services/state";

// const theme = createMuiTheme({
//   palette: {
//     type: "light",
//     primary: {
//       main: "#ababab",
//       contrastText: "#fff",
//     },
//     secondary: {
//       main: "#14a99d",
//       contrastText: "#fff",
//     },
//   },
//   overrides: {
//     MuiButton: {
//       containedPrimary: {
//         color: "white",
//       },
//       containedSecondary: {
//         color: "white",
//       },
//     },
//   },
// });

const App = ({ dispatch, context }) => {
  const navigate = useNavigate();

  const { loggedIn, loggedInAsAdmin, loaded, currentClub } = context;

  const Loading = ({ children }) => (!loaded ? <>{children}</> : null);
  const Unauthorized = ({ children }) =>
    loaded && !loggedIn && !loggedInAsAdmin ? <>{children}</> : null;
  const AuthorizedWithoutClub = ({ children }) =>
    loaded && loggedIn && currentClub && !currentClub.id ? (
      <>{children}</>
    ) : null;
  const AuthorizedWithClub = ({ children }) =>
    loaded && loggedIn && currentClub && currentClub.id ? (
      <>{children}</>
    ) : null;
  const AuthorizedAdmin = ({ children }) =>
    loaded && loggedInAsAdmin && !loggedIn ? <>{children}</> : null;

  const init = useCallback(
    async ({ dispatch, context }) => {
      const { loaded } = context;
      if (loaded) {
        return false;
      }

      let organization = window.location.hostname.split(".")[0];
      let organizationSlug = null;

      if (organization === "app" || organization === "localhost") {
        organizationSlug = "slate";
      } else if (organization) {
        organizationSlug = organization;
      } else {
        organizationSlug = "slate";
      }

      let siteLoadedPayload = {
        type: "siteLoaded",
        loaded: true,
      };

      let organizationData = null;

      try {
        organizationData = await apiGetOrganizationData(organizationSlug);

        const existingTokensStr = localStorage.getItem(
          "slate-player-web-app-tokens",
        );

        if (existingTokensStr) {
          let existingTokens = null;
          try {
            existingTokens = JSON.parse(existingTokensStr);
            const data = await apiRefreshToken(existingTokens.refreshToken);

            if (data.accessToken) {
              siteLoadedPayload.loggedIn = true;

              dispatch({
                type: "storeToken",
                tokens: {
                  accessToken: data.accessToken,
                  refreshToken: data.refreshToken,
                },
              });

              // Decode the tokens to see if there is a clubId and playerId
              let decodedAccessToken = jwtDecode(data.accessToken);

              const getProfileData = await apiGetProfile();

              siteLoadedPayload.profile = getProfileData;

              if (decodedAccessToken.playerId) {
                siteLoadedPayload.currentPlayer = getProfileData.players.find(
                  (player) => player.id === decodedAccessToken.playerId,
                );
              } else {
                let eligiblePlayerMemberships = getProfileData.players.filter(
                  (player) =>
                    player.memberships &&
                    player.memberships.filter(
                      (_) =>
                        _.enabled && _.organizationId === organizationData.guid,
                    ).length > 0,
                );
                siteLoadedPayload.currentPlayer = eligiblePlayerMemberships[0];
              }

              if (
                decodedAccessToken.clubId &&
                siteLoadedPayload.currentPlayer
              ) {
                const getPlayerDetails = await apiGetPlayerDetails(
                  decodedAccessToken.playerId,
                );
                siteLoadedPayload.currentPlayer = getPlayerDetails;

                siteLoadedPayload.currentClub =
                  siteLoadedPayload.currentPlayer.memberships
                    .filter(
                      (_) =>
                        _.enabled && _.organizationId === organizationData.id,
                    )
                    .find(
                      (membership) =>
                        membership.clubId === decodedAccessToken.clubId,
                    );
              } else if (siteLoadedPayload.currentPlayer) {
                siteLoadedPayload.currentClub =
                  siteLoadedPayload.currentPlayer.memberships.filter(
                    (_) =>
                      _.enabled && _.organizationId === organizationData.guid,
                  )[0];

                if (siteLoadedPayload.currentClub) {
                  const loginToClubData = await apiLoginToClub(
                    siteLoadedPayload.currentClub.clubId,
                    siteLoadedPayload.currentPlayer.id,
                  );

                  dispatch({
                    type: "storeToken",
                    tokens: {
                      accessToken: loginToClubData.accessToken,
                      refreshToken: loginToClubData.refreshToken,
                    },
                  });

                  const getPlayerDetails = await apiGetPlayerDetails(
                    siteLoadedPayload.currentPlayer.id,
                  );
                  siteLoadedPayload.currentPlayer = getPlayerDetails;

                  // Fetch full club details including stripeAccountId
                  const clubDetails = await apiGetClub(siteLoadedPayload.currentClub.clubId);
                  console.log("clubDetails:", clubDetails);
                  if (clubDetails && !clubDetails.reason) {
                    siteLoadedPayload.currentClub = {
                      ...siteLoadedPayload.currentClub,
                      ...clubDetails,
                      // Map camelCase field names for consistency
                      stripeAccountId: clubDetails.StripeAccountId || clubDetails.stripeAccountId,
                      isGoCardlessSetup: clubDetails.IsGoCardlessSetup || clubDetails.isGoCardlessSetup,
                      isStripeSetup: clubDetails.IsStripeSetup || clubDetails.isStripeSetup,
                    };
                    console.log("Updated currentClub:", siteLoadedPayload.currentClub);
                  }
                }
              } else {
                navigate("/no-clubs");
              }
            }
          } catch (e) {
            console.log(e);
          }
        }
      } catch (e) {
        console.log(e);
      }

      siteLoadedPayload.organizationData = organizationData;

      dispatch(siteLoadedPayload);
    },
    [navigate],
  );

  const logout = useCallback(() => {
    dispatch({
      type: "clearToken",
    });
    dispatch({
      type: "clearSessionVariables",
    });
    dispatch({
      type: "clearProfile",
    });
    navigate("/");
    localStorage.removeItem("slate-player-web-app-tokens");
  }, [dispatch, navigate]);

  const refreshToken = useCallback(async () => {
    try {
      console.log("Attempt to refresh token");
      console.log("loggedIn", loggedIn);
      if (loggedIn) {
        const existingTokensStr = localStorage.getItem(
          "slate-player-web-app-tokens",
        );
        if (!existingTokensStr) return;

        const existingTokens = JSON.parse(existingTokensStr);
        console.log("existingTokens", existingTokens);
        const data = await apiRefreshToken(existingTokens.refreshToken);
        console.log("data", data);

        if (data.accessToken) {
          const newTokens = {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
          };

          dispatch({
            type: "storeToken",
            tokens: newTokens,
          });

          localStorage.setItem(
            "slate-player-web-app-tokens",
            JSON.stringify(newTokens),
          );
        } else {
          logout();
        }
      }
    } catch (error) {
      logout();
    }
  }, [dispatch, loggedIn, logout]);

  useEffect(() => {
    refreshToken(); // Initial call on mount

    const intervalId = setInterval(refreshToken, 5 * 60 * 1000); // Every 5 minutes

    // Listen for when the user comes back to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshToken(); // Refresh immediately when tab becomes active
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch, loggedIn, refreshToken]);

  useEffect(() => {
    init({ dispatch, context });
  }, [context, dispatch, init]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <div className="App">
        <Loading>
          <Routes>
            <Route path="*" element={<SplashPage />} />
          </Routes>
        </Loading>

        <Unauthorized>
          <Routes>
            <Route
              exact
              path="/godebit/mandate_thanks"
              element={<CompleteMandateSetupPage />}
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route
              exact
              path="/forgot-password"
              element={<ForgotPasswordPage />}
            />
            <Route
              exact
              path="/password-reset/:code"
              element={<ResetPasswordPage />}
            />
            <Route
              exact
              path="/accept-invite/:code"
              element={<AcceptInvitePage />}
            />
            <Route
              exact
              path="/verify-account/:code"
              element={<VerifyAccountPage />}
            />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Unauthorized>

        <AuthorizedWithoutClub>
          <Routes>
            <Route path="/" element={<SplashPage />} />
            <Route path="/no-clubs" element={<NoClubsPage />} />
            <Route
              exact
              path="/verify-account/:code"
              element={<VerifyAccountPage />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthorizedWithoutClub>

        <AuthorizedWithClub>
          <Routes>
            <Route
              exact
              path="/godebit/mandate_thanks"
              element={<CompleteMandateSetupPage />}
            />
            <Route
              exact
              path="/verify-account/:code"
              element={<VerifyAccountPage />}
            />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/events/admin" element={<AdminEventsPage />} />
            <Route path="/events/new" element={<CreateEventPage />} />
            <Route
              exact
              path="/events/:eventId/edit"
              element={<EditEventPage />}
            />
            <Route path="/events/:eventId" element={<EventPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/archive" element={<ArchivePage />} />
            <Route path="/archive/admin" element={<ArchivePage />} />
            <Route
              exact
              path="/archive/:eventId"
              element={<ArchivedEventPage />}
            />
            <Route
              path="/availability-requests/new"
              element={<CreateAvailabilityRequestPage />}
            />
            <Route
              path="/availability-requests/:availabilityRequestId"
              element={<AvailabilityRequestPage />}
            />
            <Route
              exact
              path="/availability-requests/:availabilityRequestId/edit"
              element={<EditAvailabilityRequestPage />}
            />
            <Route path="/availability" element={<AvailabilityPage />} />
            <Route
              path="/availability/admin"
              element={<AdminAvailabilityPage />}
            />
            <Route
              exact
              path="/outstanding-fees"
              element={<OutstandingFeesPage />}
            />
            {/* <Route path="/" element={<HomePage/>} /> */}
            <Route path="*" element={<Navigate to="/events" replace />} />
          </Routes>
        </AuthorizedWithClub>

        <AuthorizedAdmin>
          <Routes>
            <Route
              exact
              path="/select-player"
              element={<AdminSelectUserPage />}
            />
            <Route
              path="*"
              element={<Navigate to="/select-player" replace />}
            />
          </Routes>
        </AuthorizedAdmin>
      </div>
    </LocalizationProvider>
  );
};

export default withState(App);
